const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Returns the day number within the year (1-based).
 * Ported from missioncontrol/src/utils.ts
 */
export function daysIntoYear(date: Date): number {
	return (
		(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) - Date.UTC(date.getFullYear(), 0, 0)) /
		24 /
		60 /
		60 /
		1000
	);
}

/**
 * Returns the last digit of the year as a string.
 * Ported from missioncontrol/src/utils.ts
 */
export function getLastDigitOfYear(date: Date): string {
	return date.getFullYear().toString().split("").pop()!;
}

/**
 * Returns the week number within the year (1-based).
 * Week 1 = days 1-7, Week 2 = days 8-14, etc.
 */
export function weekOfYear(date: Date): number {
	return Math.ceil(daysIntoYear(date) / 7);
}

/**
 * Generates the daily tuning (ops) password for a given operator and date.
 * Algorithm: parseInt(`${userId}${dayOfYear}${lastDigitOfYear}`) → hex → last 4 chars
 */
export function generateTuningPassword(appUserId: number, date: Date): string {
	const raw = parseInt(`${appUserId}${daysIntoYear(date)}${getLastDigitOfYear(date)}`);
	const hex = raw.toString(16).toLowerCase();
	return hex.slice(-4).padStart(4, "0");
}

/**
 * Generates the daily engineering (technician) password for a given operator and date.
 * Algorithm: same as tuning but add 0xEE (238) before hex conversion.
 */
export function generateEngineeringPassword(appUserId: number, date: Date): string {
	const raw = parseInt(`${appUserId}${daysIntoYear(date)}${getLastDigitOfYear(date)}`) + 0xee;
	const hex = raw.toString(16).toLowerCase();
	return hex.slice(-4).padStart(4, "0");
}

/**
 * Generates the weekly technician password (not operator-specific).
 * Algorithm: parseInt(`${weekOfYear}${lastDigitOfYear}`) + 0xEE → hex → last 4 chars
 */
export function generateWeeklyTechPassword(date: Date): string {
	const raw = parseInt(`${weekOfYear(date)}${getLastDigitOfYear(date)}`) + 0xee;
	const hex = raw.toString(16).toLowerCase();
	return hex.slice(-4).padStart(4, "0");
}

function formatDate(date: Date): string {
	return `${MONTH_NAMES[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

/**
 * Returns validity period string for a daily password.
 * e.g. "Valid: Feb 23, 2026, 12:00 AM – 11:59 PM"
 */
export function getDailyValidPeriod(date: Date): string {
	return `Valid: ${formatDate(date)}, 12:00 AM – 11:59 PM`;
}

/**
 * Returns validity period string for a weekly password.
 * e.g. "Valid: Feb 17, 2026, 12:00 AM – Feb 23, 2026, 11:59 PM"
 */
export function getWeeklyValidPeriod(date: Date): string {
	const week = weekOfYear(date);
	const year = date.getFullYear();

	// Week N covers days (N-1)*7+1 through N*7
	const startDay = (week - 1) * 7 + 1;
	const endDay = week * 7;

	const startDate = dayOfYearToDate(year, startDay);

	// Clamp end day to last day of year
	const lastDayOfYear = isLeapYear(year) ? 366 : 365;
	const clampedEndDay = Math.min(endDay, lastDayOfYear);
	const endDate = dayOfYearToDate(year, clampedEndDay);

	return `Valid: ${formatDate(startDate)}, 12:00 AM – ${formatDate(endDate)}, 11:59 PM`;
}

function dayOfYearToDate(year: number, dayOfYear: number): Date {
	const date = new Date(year, 0, 1);
	date.setDate(dayOfYear);
	return date;
}

function isLeapYear(year: number): boolean {
	return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}
